import { Box, Button } from '@mui/material';
import React, { FunctionComponent } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/es';
import { AuditTrailEntry, AuditTrailResponse } from '../../../types/claim';

import './index.scss';
import AuditTrailMetrics from './CPEAuditTrailMetrics';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('en');

interface CPEAuditTrailProps {
  auditTrail: AuditTrailResponse;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: 'white',
    color: '#6D788B',
    fontStyle: 'normal',
    fontSize: '14px',
    letterSpacing: '0.5px',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight: '400',
    color: '#3A3541',
  },
}));

interface TypeCellProps {
  entryType: 'action' | 'rule';
  isTerminate?: boolean;
}

const TypeCell = styled(StyledTableCell, {
  shouldForwardProp: (prop) => prop !== 'entryType' && prop !== 'isTerminate',
})<TypeCellProps>(({ entryType, isTerminate }) => ({
  position: 'relative',
  textTransform: 'capitalize',
  transition: 'all 0.2s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '4px',
    height: '70%',
    borderRadius: '0 2px 2px 0',
    ...(entryType === 'action' && {
      backgroundColor: '#7367F0',
    }),
    ...(entryType === 'rule' && {
      backgroundColor: '#28C76F',
    }),
  },
  ...(isTerminate && {
    backgroundColor: 'rgba(220, 53, 69, 0.08)',
  }),
  ...(!isTerminate && {
    ...(entryType === 'action' && {
      backgroundColor: 'rgba(115, 103, 240, 0.08)',
    }),
    ...(entryType === 'rule' && {
      backgroundColor: 'rgba(40, 199, 111, 0.08)',
    }),
  }),
}));

const StyledTableRow = styled(TableRow)<{ isTerminate?: boolean }>(
  ({ isTerminate }) => ({
    '&:last-child td, &:last-child th': {
      border: 0,
    },
    ...(isTerminate && {
      td: {
        borderBottom: '1px solid rgba(220, 53, 69, 0.2)',
      },
    }),
  }),
);

const ResultChip = styled(Box)<{ result?: boolean }>(({ result }) => {
  if (result === undefined) {
    return {};
  }

  return {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
    ...(result === true && {
      backgroundColor: '#E8F5E9',
      color: '#2E7D32',
    }),
    ...(result === false && {
      backgroundColor: '#FEECEB',
      color: '#DC3545',
    }),
  };
});

interface TypeChipProps {
  entryType: 'action' | 'rule';
  isTerminate?: boolean;
}

const TypeChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'entryType' && prop !== 'isTerminate',
})<TypeChipProps>(({ entryType, isTerminate }) => ({
  display: 'inline-block',
  padding: '4px 12px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'uppercase',
  ...(entryType === 'action' && {
    backgroundColor: 'rgba(115, 103, 240, 0.12)',
    color: '#7367F0',
  }),
  ...(entryType === 'rule' && {
    backgroundColor: 'rgba(40, 199, 111, 0.12)',
    color: '#28C76F',
  }),
}));

const NameCell = styled(StyledTableCell)<{ isTerminate?: boolean }>(
  ({ isTerminate }) => ({
    ...(isTerminate && {
      fontWeight: '600 !important',
      color: '#DC3545 !important',
    }),
  }),
);

const CPEAuditTrail: FunctionComponent<AuditTrailResponse> = ({
  entries,
}): JSX.Element => {
  const formatDate = (dateString: string) => {
    try {
      const date = dayjs(dateString).local();

      if (!date.isValid()) {
        console.error('Invalid date:', dateString);

        return 'Invalid date';
      }

      return date.format('DD MMMM YYYY, HH:mm:ss');
    } catch (error) {
      console.error('Error formatting date:', error);

      return 'Invalid date';
    }
  };

  const downloadCSV = () => {
    try {
      const headers = [
        'DateTime',
        'Decision Tree',
        'Stage',
        'Type',
        'Name',
        'Result',
        'Description',
      ];

      const csvContent = entries.map((entry: AuditTrailEntry) => [
        formatDate(entry.createdAt),
        entry.company,
        entry.policyType,
        entry.stage,
        entry.type,
        entry.name,
        entry.result !== undefined ? (entry.result ? 'PASSED' : 'FAILED') : '',
        entry.description,
      ]);

      const allRows = [headers, ...csvContent];

      const csvString = allRows
        .map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','),
        )
        .join('\n');

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `audit-trail-${dayjs().format('YYYY-MM-DD-HH-mm-ss')}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }
  };

  return (
    <Box sx={{ padding: '1rem' }}>
      {entries.length > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              onClick={downloadCSV}
              startIcon={<FileDownloadIcon />}
              sx={{
                backgroundColor: '#7367F0',
                '&:hover': {
                  backgroundColor: '#655BD3',
                },
              }}
            >
              Export CSV
            </Button>
          </Box>
          <AuditTrailMetrics entries={entries} />
        </>
      )}
      <TableContainer
        component={Paper}
        className="TableContainer"
        sx={{
          boxShadow: '0px 2px 10px rgba(76, 78, 100, 0.22)',
          borderRadius: '6px',
        }}
      >
        <Table sx={{ minWidth: 700 }} aria-label="audit trail table">
          <TableHead>
            <TableRow>
              <StyledTableCell>DateTime</StyledTableCell>
              <StyledTableCell align="left">Company</StyledTableCell>
              <StyledTableCell align="left">Policy Type</StyledTableCell>
              <StyledTableCell align="left">Stage</StyledTableCell>
              <StyledTableCell align="left">Type</StyledTableCell>
              <StyledTableCell align="left">Name</StyledTableCell>
              <StyledTableCell align="left">Result</StyledTableCell>
              <StyledTableCell align="left">Description</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length > 0 ? (
              entries.map((entry: AuditTrailEntry) => {
                const isTerminate =
                  entry.type === 'action' && entry.name === 'terminate';

                return (
                  <StyledTableRow key={entry.id} isTerminate={isTerminate}>
                    <StyledTableCell>
                      {formatDate(entry.createdAt)}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {entry.company}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {entry.policyType}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {entry.stage}
                    </StyledTableCell>
                    <TypeCell
                      align="left"
                      entryType={entry.type}
                      isTerminate={isTerminate}
                    >
                      <TypeChip
                        entryType={entry.type}
                        isTerminate={isTerminate}
                      >
                        {entry.type}
                      </TypeChip>
                    </TypeCell>
                    <NameCell align="left" isTerminate={isTerminate}>
                      {entry.name}
                    </NameCell>
                    <StyledTableCell align="left">
                      {entry.result !== undefined && (
                        <ResultChip result={entry.result}>
                          {entry.result ? 'PASSED' : 'FAILED'}
                        </ResultChip>
                      )}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {entry.description}
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8}>
                  <Box
                    sx={{
                      padding: '20px',
                      textAlign: 'center',
                      color: '#6D788B',
                      fontSize: '14px',
                    }}
                  >
                    No information available
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CPEAuditTrail;
